import { Component, Renderer2, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import BusinessEntity from './core/models/business.model';
import { FirebaseNotificationsMessagingService } from './core/services/firebase-messaging.service';
import { PreviousRouteService } from './core/services/previous-route.service';
import { LocationStrategy, Location } from '@angular/common';

declare var $: any;

/**
 * @component AppComponent
 * @selector app-root
 * @implements {OnInit}
 *
 * @description
 * The `AppComponent` is the root component of the Aqua Cloud application. It serves as the main entry point
 * and container for the application's view hierarchy.
 *
 * ## Key Responsibilities:
 * 1. **Global Initialization**: Sets up global event listeners (keyboard shortcuts) and UI behaviors (custom scrollbars).
 * 2. **Authentication Integration**: Handles initial parameters from external sources (e.g., Ionic mobile app) via URL query parameters.
 * 3. **Session & Navigation Management**: Monitors router events to handle session validation, account status checks (inactivated accounts), and navigation redirects.
 * 4. **UI Cleanup**: Manages global UI cleanup tasks, such as removing modal backdrops on navigation.
 * 5. **Notification Setup**: Initiates permission requests for Firebase push notifications.
 *
 * ## Features:
 * - **Ionic Integration**: Captures `mobileNumber`, `password`, and `redirect` params from the URL for seamless mobile app integration.
 * - **Security**: Prevents common browser keyboard shortcuts (Ctrl+A, Ctrl+C, Ctrl+S, Ctrl+P, Ctrl+U) to protect content.
 * - **Account Status Monitoring**: Automatically logs out users and redirects to login if their business account is inactivated (except for specific routes).
 * - **Custom Scrollbars**: Initializes `mCustomScrollbar` for elements with the `.scroll` class.
 *
 * ## LocalStorage Dependencies:
 * - `ionicMobileNumber`: Stored from URL param.
 * - `ionicPassword`: Stored from URL param.
 * - `redirect`: Stored from URL param.
 * - `isLoggedInMobile`: Checked for login status.
 * - `loggedInBusiness`: Parsed to check account status.
 * - `isSessionExist`: Checked to verify active session.
 * - `loginPersonType`: Checked to determine user role.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  /**
   * @type {Subscription}
   * Subscription to router events. Used to handle logic on NavigationEnd.
   */
  public routerSubscription: Subscription;

  /**
   * @type {BusinessEntity}
   * The business entity associated with the current session.
   * Used to check account status (e.g., 'inactivated').
   */
  public businessEntity: BusinessEntity;

  /**
   * @type {string}
   * Flag indicating if a user session currently exists in localStorage.
   */
  public isSessionExist: string;

  /**
   * @type {string}
   * The type of the logged-in person (e.g., 'EMPLOYEE', 'Farmer').
   */
  public loginPersonType: string;

  /**
   * @type {string}
   * Flag indicating if the user is logged in via mobile.
   */
  isLogin: string;

  /**
   * Constructor for AppComponent.
   * Initializes services.
   *
   * @param {Location} location - Service for interacting with the browser's URL.
   * @param {ActivatedRoute} route - Service for retrieving information about the route associated with the component.
   * @param {FirebaseNotificationsMessagingService} firebaseMessagingService - Service for handling Firebase push notifications.
   * @param {Router} router - Service for navigation and URL manipulation.
   * @param {Renderer2} renderer - Service for custom rendering and DOM manipulation.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the URL path.
   *
   * @service Location
   * @service ActivatedRoute
   * @service FirebaseNotificationsMessagingService
   * @service Router
   * @service Renderer2
   * @service PreviousRouteService
   * @service LocationStrategy
   */
  constructor(private location: Location, public route: ActivatedRoute, private firebaseMessagingService: FirebaseNotificationsMessagingService, public router: Router, private renderer: Renderer2,
    public previousRouteService: PreviousRouteService, public locationStrategy: LocationStrategy) {
  }

  /**
   * @lifecycle ngOnInit
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   *
   * ## Logic Flow:
   * 1. **URL Param Parsing**: Extracts `mobileNumber`, `password`, and `redirect` from the URL query string and stores them in `localStorage`. This supports integration with the Ionic mobile app.
   * 2. **Keyboard Listeners**: Calls `setupKeyboardEventListener` to block specific shortcuts.
   * 3. **Notifications**: Requests Firebase notification permissions.
   * 4. **Router Subscription**: Subscribes to `NavigationEnd` events to:
   *    - Check `isSessionExist` and `businessEntity` status.
   *    - If the business account is 'inactivated' and the user is not an 'EMPLOYEE' and not on the account closure page:
   *      - Clears localStorage.
   *      - Redirects to `/login`.
   *      - Cleans up modal classes and backdrops.
   *    - Generally cleans up modal backdrops on navigation to prevent UI locking.
   *
   * @side-effect Modifies `localStorage`, navigates router, modifies DOM (modal cleanup).
   */
  public ngOnInit(): void {
    const fullUrl = this.location.path();  // Get the full URL after the hash (#)
    const urlParams = new URLSearchParams(fullUrl.split('?')[1]);
    let mobileNumber = urlParams.get('mobileNumber');
    let password = urlParams.get('password');
    let redirect = urlParams.get('redirect');
    localStorage.setItem('ionicMobileNumber', mobileNumber);
    localStorage.setItem('ionicPassword', password);
    localStorage.setItem('redirect', redirect);

    this.isLogin = localStorage.getItem('isLoggedInMobile');
    // Set up preventing keyboard event listeners
    this.setupKeyboardEventListener();
    this.firebaseMessagingService.requestNotificationPermission();
    this.businessEntity = JSON.parse(localStorage.getItem('loggedInBusiness'));
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isSessionExist = localStorage.getItem('isSessionExist');
        this.businessEntity = JSON.parse(localStorage.getItem('loggedInBusiness'));
        this.loginPersonType = localStorage.getItem('loginPersonType');
        if (this.businessEntity && this.businessEntity.accountStatus && this.businessEntity.accountStatus == 'inactivated' && !event.url.includes('/home/account-closure') && this.loginPersonType != 'EMPLOYEE') {
          if (this.isSessionExist == 'yes') {
            // To clear the localstorage data
            localStorage.clear();
            // To Navigate to the login screen
            this.router.navigate(['/login']);
            // Remove the modal-open class from the body tag
            this.renderer.removeClass(document.body, 'modal-open');
            // Remove the modal backdrop element
            const modalBackdrop = document.querySelector('.modal-backdrop');
            if (modalBackdrop) {
              this.renderer.removeChild(document.body, modalBackdrop);
            }
          }
        } else {
          // Remove the modal-open class from the body tag
          this.renderer.removeClass(document.body, 'modal-open');
          // Remove the modal backdrop element
          const modalBackdrop = document.querySelector('.modal-backdrop');
          if (modalBackdrop) {
            this.renderer.removeChild(document.body, modalBackdrop);
          }
        }
      }
    });
  }

  /**
   * @lifecycle ngAfterViewInit
   * @description
   * Lifecycle hook that is called after a component's view has been fully initialized.
   * Initializes the custom scrollbar plugin (`mCustomScrollbar`) for elements with the class `.scroll`.
   */
  public ngAfterViewInit() {
    if ($('.scroll').length > 0) {
      $('.scroll').mCustomScrollbar({
        axis: 'y',
        autoHideScrollbar: true,
        scrollInertia: 20,
        advanced: { autoScrollOnFocus: false }
      });
    }
  }

  /**
   * @method setupKeyboardEventListener
   * @private
   * @description
   * Sets up a global keydown event listener to prevent specific keyboard shortcuts.
   * Blocked shortcuts (with Ctrl key): 'a' (Select All), 'c' (Copy), 's' (Save), 'p' (Print), 'u' (View Source).
   *
   * @side-effect Adds an event listener to the document.
   */
  private setupKeyboardEventListener() {
    document.addEventListener('keydown', (event) => {
      if (['a', 'c', 's', 'p', 'u'].includes(event.ctrlKey && event.key)) {
        event.preventDefault(); // Prevent default behavior
      }
    });
  }
}
